import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import StatusChip from '@/components/recovery/StatusChip';
import { backendUrl, fmtIST } from '@/components/recovery/recoveryUi';
import PrintToggle from '@/components/recovery/PrintToggle';

const H = ({ children }) => <div className="mt-4 mb-1.5 text-[10.5px] font-extrabold uppercase tracking-[.14em] text-[#07122f]">{children}</div>;

function AlertRow({ a, onAck }) {
  const [note, setNote] = useState('');
  const urgent = a.stage === 'escalated' && !a.acknowledged;
  return (
    <li className={`rounded-lg border p-2 text-[12.5px] ${urgent ? 'border-red-300 bg-red-50' : 'border-slate-200'}`} data-testid={`rec-alert-${a.id}`}>
      <div className="flex items-center gap-2">
        <b className="flex-1">{a.title}</b>
        <span className="text-[10.5px] uppercase font-bold text-slate-500">{a.kind.replace('_', ' ')} · {a.stage}</span>
      </div>
      <div className="text-[11px] text-slate-500">{fmtIST(a.created_at)}{a.acknowledged ? ` · acknowledged by ${a.ack_by} ${fmtIST(a.ack_at)}${a.ack_note ? ` — “${a.ack_note}”` : ''}` : ''}</div>
      {!a.acknowledged && a.stage !== 'resolved' && (
        <div className="mt-1.5 flex gap-1.5">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note to patient" className="flex-1 px-2 py-1 rounded border border-slate-300 text-[12px]" data-testid={`rec-alert-note-${a.id}`} />
          <button type="button" onClick={() => onAck(a.id, note)} className="px-3 py-1 rounded bg-[#07122f] text-white text-[12px] font-bold" data-testid={`rec-alert-ack-${a.id}`}>Acknowledge</button>
        </div>
      )}
    </li>
  );
}

export default function RecoveryDetail({ planId, refreshKey = 0 }) {
  const [d, setD] = useState(null);
  const load = useCallback(async () => { try { const { data } = await api.get(`/surgical/recovery/${planId}/detail`); setD(data); } catch { toast.error('Could not load recovery detail'); } }, [planId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const ack = async (id, note) => { try { await api.post(`/surgical/recovery/alerts/${id}/ack`, { note }); toast.success('Acknowledged'); load(); } catch { toast.error('Could not acknowledge'); } };
  if (!d) return <div className="py-6 text-center text-slate-500"><Loader2 className="inline animate-spin" /></div>;
  return (
    <div className="text-[13px]" data-testid="rec-detail">
      <div className="flex items-center gap-2 flex-wrap">
        <StatusChip status={d.status_chip} testId="rec-detail-status" />
        <span className="font-bold">Day {d.day}</span>
        <span className="text-slate-500">Next check-in: {d.next_checkin_day == null ? '—' : `Day ${d.next_checkin_day}`}</span>
        {d.notif_state !== 'granted' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-[10.5px] font-bold" data-testid="rec-detail-reminders-off"><BellOff size={11} /> Reminders OFF</span>}
        {!d.patient_linked && <span className="text-[11px] text-amber-700">Patient has not opened the link yet</span>}
      </div>
      <H>Medicine adherence</H>
      <div className="space-y-1.5" data-testid="rec-adherence">
        {d.adherence.length === 0 && <div className="text-slate-400">No doses due yet.</div>}
        {d.adherence.map((m) => (
          <div key={m.med_key} className="flex items-center gap-2">
            <span className="w-40 truncate font-semibold">{m.name}</span>
            <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${m.due ? (100 * m.taken) / m.due : 0}%` }} /></div>
            <span className="text-[11px] w-24 text-right">{m.taken}/{m.due} taken{m.missed ? ` · ${m.missed} missed` : ''}</span>
          </div>
        ))}
      </div>
      <H>Milestones</H>
      <ul className="grid md:grid-cols-2 gap-1">
        {d.plan.milestones.map((m) => <li key={m.id} className={d.milestones_reached[m.id] ? 'text-emerald-700' : 'text-slate-600'}>{d.milestones_reached[m.id] ? '✓' : '○'} Day {m.target_day} · {m.title}</li>)}
      </ul>
      <H>Alerts</H>
      <ul className="space-y-1.5">{d.alerts.length ? d.alerts.map((a) => <AlertRow key={a.id} a={a} onAck={ack} />) : <li className="text-slate-400">No alerts.</li>}</ul>
      <H>Rx versions</H>
      <ul className="space-y-1" data-testid="rec-detail-rx">
        {d.rx_versions.map((r) => (
          <li key={r.rxId} className="flex items-center gap-2">
            <a href={backendUrl(r.downloadUrl)} target="_blank" rel="noopener noreferrer" className="font-mono text-teal-700">{r.rxId}</a>
            <span className={`text-[11px] font-bold ${r.status === 'issued' ? 'text-emerald-700' : 'text-slate-400'}`}>{r.status}</span>
            <span className="text-[11px] text-slate-500">issued {fmtIST(r.issuedAt)} · Po {r.patientOpenedAt ? fmtIST(r.patientOpenedAt) : 'not yet'}</span>
          </li>
        ))}
      </ul>
      <H>Check-in history</H>
      <ul className="space-y-1" data-testid="rec-detail-checkins">
        {d.checkins.length === 0 && <li className="text-slate-400">No check-ins yet.</li>}
        {d.checkins.map((c) => <li key={c.id}>Day {c.day} · {fmtIST(c.ts)} · missed meds {c.meds_missed.length} · red flags {c.red_flags_yes.length}{c.note ? ` · “${c.note}”` : ''}</li>)}
      </ul>
      <details className="mt-4"><summary className="cursor-pointer text-[11px] font-extrabold uppercase tracking-[.14em]" data-testid="rec-detail-audit-toggle">Audit trail ({d.events.length})</summary>
        <ul className="mt-2 space-y-0.5 text-[11.5px] text-slate-600" data-testid="rec-detail-audit">
          {d.events.map((e) => <li key={e.id}>{fmtIST(e.ts)} · <b>{e.actor_name}</b> ({e.actor_type}) · {e.action.replace(/_/g, ' ')}{e.detail ? ` — ${e.detail}` : ''}</li>)}
        </ul>
      </details>
      <PrintToggle testPrefix="rec-detail" data={{ patientName: d.patient_name, operation: d.operation, hospital: d.hospital_name, surgeon: d.surgeon?.name, plan: d.plan, times: d.reminder_times }} />
    </div>
  );
}
